import type { FC, ReactNode } from 'react'
import type { Experience } from '@/types'

import { useRef } from 'react'
import { gsap, ScrollTrigger, useGSAP } from '@/lib/motion/gsap'
import { cms } from '@/lib/cms'
import { pad } from '@/lib/format'
import { useReveal } from '@/hooks/use-reveal'
import { Eyebrow } from '@/components/generals/section-heading'
import { SplitWords } from '@/components/generals/split-words'
import { experienceContent } from '@/constants/content'

interface ExperienceTimelineProps {
    experiences: Experience[]
    index?: string
}

/** Sticky intro on the left, roles on the right that light up one by one while scrolling. */
export const ExperienceTimeline: FC<ExperienceTimelineProps> = ({ experiences = [], index = '05' }): ReactNode => {
    const scope = useRef<HTMLElement>(null)
    const counterRef = useRef<HTMLSpanElement>(null)

    useReveal(scope, [experiences.length])

    useGSAP(() => {
        const root = scope.current
        if (!root) return

        const steps = Array.from(root.querySelectorAll<HTMLElement>('[data-step]'))
        const list = root.querySelector('[data-steps]')
        const bar = root.querySelector('[data-step-bar]')

        if (steps.length === 0) return

        const setActive = (activeIndex: number): void => {
            steps.forEach((step, stepIndex) => {
                step.dataset.active = stepIndex === activeIndex ? 'true' : 'false'
            })

            if (counterRef.current) counterRef.current.textContent = pad(activeIndex + 1)
        }

        setActive(0)

        const triggers = steps.map((step, stepIndex) => ScrollTrigger.create({
            trigger: step,
            start: 'top 60%',
            end: 'bottom 60%',
            onToggle: (self) => {
                if (self.isActive) setActive(stepIndex)
            }
        }))

        const mm = gsap.matchMedia()

        mm.add('(prefers-reduced-motion: no-preference)', () => {
            if (bar && list) {
                gsap.fromTo(bar, { scaleY: 0 }, {
                    scaleY: 1,
                    ease: 'none',
                    scrollTrigger: { trigger: list, start: 'top 60%', end: 'bottom 60%', scrub: true }
                })
            }
        })

        return () => {
            triggers.forEach(trigger => trigger.kill())
            mm.revert()
        }
    }, { scope, dependencies: [experiences.length] })

    if (experiences.length === 0) return null

    return (
        <section ref={scope} id='experience' aria-labelledby='experience-title' className='relative bg-ink py-28 text-bone md:py-40'>
            <div className='gutter grid gap-16 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-5'>
                    <div className='flex flex-col gap-8 lg:sticky lg:top-32'>
                        <Eyebrow index={index} className='text-stone'>
                            {experienceContent.label}
                        </Eyebrow>

                        <h2 id='experience-title' className='font-wide text-big font-semibold uppercase tracking-[-0.05em]' data-reveal='words'>
                            <SplitWords text={experienceContent.title} />{' '}
                            <SplitWords text={experienceContent.accent} wordClassName='font-serif font-normal normal-case italic tracking-[-0.02em] text-flare' />
                        </h2>

                        <p className='max-w-sm text-lead text-bone/85' data-reveal='fade'>
                            {experienceContent.subtitle}
                        </p>

                        <p className='max-w-sm text-sm leading-relaxed text-stone' data-reveal='fade'>
                            {experienceContent.description}
                        </p>

                        <div className='mt-4 hidden items-end gap-4 lg:flex' aria-hidden='true'>
                            <span ref={counterRef} className='font-wide text-7xl font-semibold leading-none tracking-[-0.06em] text-flare'>
                                01
                            </span>
                            <span className='eyebrow pb-2 text-stone'>
                                / {pad(experiences.length)} {experienceContent.stagesLabel}
                            </span>
                        </div>
                    </div>
                </div>

                <div className='relative lg:col-span-6 lg:col-start-7'>
                    <div className='absolute bottom-0 left-0 top-0 hidden w-px bg-bone/15 md:block' aria-hidden='true'>
                        <div className='h-full w-full origin-top bg-flare' data-step-bar='' />
                    </div>

                    <ol className='flex flex-col md:pl-12' data-steps=''>
                        {experiences.map((experience, stepIndex) => (
                            <li
                                key={experience.id}
                                data-step=''
                                data-active='false'
                                className='group flex flex-col gap-6 border-b border-bone/10 py-12 transition-opacity duration-700 first:pt-0 last:border-b-0 md:min-h-[40vh] md:justify-center md:py-16 md:opacity-30 md:data-[active=true]:opacity-100'
                            >
                                <div className='flex flex-wrap items-center gap-4'>
                                    <span className='eyebrow text-flare'>
                                        {pad(stepIndex + 1)}
                                    </span>
                                    <span className='h-px w-10 bg-bone/20' aria-hidden='true' />
                                    {experience.period && (
                                        <span className='eyebrow text-stone' data-mdlfy-cms={cms('experiences', experience.id, 'period')}>
                                            {experience.period}
                                        </span>
                                    )}
                                </div>

                                <h3 className='font-wide text-[clamp(2.2rem,5vw,5.25rem)] font-semibold uppercase leading-[0.88] tracking-[-0.05em]'>
                                    <span data-mdlfy-cms={cms('experiences', experience.id, 'title')}>{experience.title}</span>
                                </h3>

                                <p className='flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-bone/80 md:text-base'>
                                    {experience.role && (
                                        <span className='font-medium text-bone' data-mdlfy-cms={cms('experiences', experience.id, 'role')}>
                                            {experience.role}
                                        </span>
                                    )}
                                    {experience.role && experience.organization && (
                                        <span className='text-stone' aria-hidden='true'>/</span>
                                    )}
                                    {experience.organization && (
                                        <span data-mdlfy-cms={cms('experiences', experience.id, 'organization')}>
                                            {experience.organization}
                                        </span>
                                    )}
                                </p>

                                {experience.description && (
                                    <p className='font-serif text-[clamp(1.4rem,2.2vw,2.2rem)] italic leading-tight text-bone/85' data-mdlfy-cms={cms('experiences', experience.id, 'description')}>
                                        {experience.description}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        </section>
    )
}
